// src/components/molecules/QuickFacetFilterButton/QuickFacetFilterButton.jsx
import React, { useState } from 'react';
import PropTypes from 'prop-types';
import {
  Badge,
  Checkbox,
  IconButton,
  ListItemIcon,
  ListItemText,
  Menu,
  MenuItem,
  Tooltip,
  useTheme
} from '@mui/material';
import {
  getOperationalMode,
  operationalComponentSurfaces,
  operationalFontSizes,
  operationalScrollbars
} from '../../../design-system/tokens';
import { useTheme as useDesignTheme } from '../../../design-system/providers/ThemeProvider';

const QuickFacetFilterButton = ({
  tooltip,
  icon,
  options = [],
  selectedValues = [],
  onChange,
  getOptionIcon,
  getOptionCount,
  isOptionDisabled,
  menuMinWidth = 160,
  className = '',
  maxBadge = 9
}) => {
  const theme = useTheme();
  const { primaryGradient } = useDesignTheme();
  const [anchorEl, setAnchorEl] = useState(null);
  const hasSelection = selectedValues.length > 0;
  const chipTokens = operationalComponentSurfaces.chip[getOperationalMode(theme.palette.mode)];

  if (!options.length) {
    return null;
  }

  const handleToggle = (value, disabled) => {
    if (disabled) return;

    const nextValues = selectedValues.includes(value)
      ? selectedValues.filter((selectedValue) => selectedValue !== value)
      : [...selectedValues, value];

    onChange?.(nextValues);
  };

  return (
    <>
      <Tooltip title={tooltip} arrow>
        <IconButton
          size="small"
          className={`quick-facet-filter-button ${className}`}
          onClick={(event) => setAnchorEl(event.currentTarget)}
          sx={{
            bgcolor: hasSelection
              ? (primaryGradient || theme.palette.primary.main)
              : chipTokens.background,
            color: hasSelection ? theme.palette.primary.contrastText : theme.palette.text.primary,
            '&:hover': {
              bgcolor: hasSelection
                ? theme.palette.primary.dark
                : chipTokens.backgroundHover
            }
          }}
        >
          <Badge badgeContent={selectedValues.length} color="error" max={maxBadge}>
            {icon}
          </Badge>
        </IconButton>
      </Tooltip>

      <Menu
        anchorEl={anchorEl}
        open={Boolean(anchorEl)}
        onClose={() => setAnchorEl(null)}
        PaperProps={{ sx: { mt: 0.5, minWidth: menuMinWidth, maxHeight: 350, ...operationalScrollbars.thin } }}
      >
        {options.map((option) => {
          const value = option.value || option;
          const label = option.label || option;
          const OptionIcon = getOptionIcon?.(value, option);
          const selected = selectedValues.includes(value);
          const count = getOptionCount?.(value, option);
          const disabled = isOptionDisabled?.(value, option, selectedValues) || false;

          return (
            <MenuItem
              key={value}
              onClick={() => handleToggle(value, disabled)}
              dense
              disabled={disabled}
              sx={{
                opacity: disabled ? 0.4 : 1,
                '&.Mui-disabled': {
                  opacity: 0.4
                }
              }}
            >
              <ListItemIcon sx={{ minWidth: 36 }}>
                <Checkbox checked={selected} sx={{ p: 0 }} disabled={disabled} />
              </ListItemIcon>
              {OptionIcon && (
                <ListItemIcon sx={{ minWidth: 32 }}>
                  <OptionIcon />
                </ListItemIcon>
              )}
              <ListItemText
                primary={label}
                secondary={count !== null && count !== undefined ? `(${count})` : null}
                secondaryTypographyProps={{
                  component: 'span',
                  sx: { ml: 1, fontSize: operationalFontSizes.xs, color: 'text.secondary' }
                }}
              />
            </MenuItem>
          );
        })}
      </Menu>
    </>
  );
};

QuickFacetFilterButton.propTypes = {
  tooltip: PropTypes.string.isRequired,
  icon: PropTypes.node.isRequired,
  options: PropTypes.array,
  selectedValues: PropTypes.array,
  onChange: PropTypes.func,
  getOptionIcon: PropTypes.func,
  getOptionCount: PropTypes.func,
  isOptionDisabled: PropTypes.func,
  menuMinWidth: PropTypes.number,
  className: PropTypes.string,
  maxBadge: PropTypes.number
};

export default QuickFacetFilterButton;
